"use client";

import type { CandidateOption, CandidateSpan } from "@fluentry/domain";
import { Badge, Button } from "@fluentry/ui";
import { ArrowRight, Pin, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Reports whether the primary pointer is coarse (touch). Lets shared workbench
 * surfaces swap hover-first guidance for tap-first guidance on phones/tablets
 * without forking the markup. Falls back to fine pointer during SSR.
 */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }

    const query = window.matchMedia("(pointer: coarse)");
    setCoarse(query.matches);

    const handleChange = (event: MediaQueryListEvent) => setCoarse(event.matches);
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  return coarse;
}

export type PopoverAnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
};

type CandidatePopoverProps = {
  anchorRect: PopoverAnchorRect | null;
  selectedSpan: CandidateSpan | null;
  competingSpans: CandidateSpan[];
  explanation?: string;
  confidence?: number;
  pinned: boolean;
  onSelectSpan: (spanId: string) => void;
  onApplyCandidate: (spanId: string, candidate: CandidateOption) => void;
  onClose: () => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
};

const POPOVER_WIDTH = 320;
const POPOVER_GAP = 14;
const POPOVER_ESTIMATED_HEIGHT = 360;
const POPOVER_VIEWPORT_MARGIN = 12;
// Mobile shells dock a bottom tab bar; reserve space so the popover never tucks behind it.
const POPOVER_BOTTOM_CLEARANCE = 84;

export function CandidatePopover({
  anchorRect,
  selectedSpan,
  competingSpans,
  explanation,
  confidence,
  pinned,
  onSelectSpan,
  onApplyCandidate,
  onClose,
  onPointerEnter,
  onPointerLeave,
}: CandidatePopoverProps) {
  const coarsePointer = useCoarsePointer();

  if (!anchorRect || !selectedSpan) {
    return null;
  }

  const style = getPopoverStyle(anchorRect);
  const surfaceMaxHeight = getPopoverMaxHeight();
  const popover = (
    <>
      {pinned ? (
        // Touch has no Esc key and no hover-out, so a transparent backdrop gives
        // pinned popovers an outside-tap dismissal path while the editor stays visible.
        <div
          data-candidate-popover-backdrop="true"
          className="fixed inset-0 z-40"
          onPointerDown={onClose}
        />
      ) : null}
      <div
        data-candidate-popover="true"
        className="fixed z-50"
        style={style}
        onMouseEnter={onPointerEnter}
        onMouseLeave={onPointerLeave}
      >
        <div
          className="overflow-hidden rounded-[1.35rem] shadow-[0_20px_60px_rgba(15,118,110,0.18)]"
          style={{
            // The popover must read as a foreground surface; translucency lets the editor bleed through and makes the suggestion stack unreadable.
            backgroundColor: "#ffffff",
            border: "1px solid rgba(15, 118, 110, 0.12)",
            maxHeight: surfaceMaxHeight,
          }}
        >
          <div className="flex items-center justify-between gap-3 border-b border-[#0f766e]/10 px-4 py-3">
            <div className="grid gap-1">
              <span className="text-[10px] font-semibold tracking-[0.22em] text-[#5f7c80] uppercase">
                Suggested Rewrite
              </span>
              <strong className="line-clamp-1 text-sm text-slate-950">
                {selectedSpan.sourceText}
              </strong>
            </div>
            <div className="flex items-center gap-2">
              {confidence != null ? (
                <Badge className="rounded-full border border-[#8fd1ca] bg-[#daf0ee] text-[10px] font-semibold tracking-[0.2em] text-[#0b6d70] uppercase shadow-none hover:bg-[#daf0ee]">
                  {`${Math.round(confidence * 100)}%`}
                </Badge>
              ) : null}
              {pinned ? (
                <Badge
                  variant="outline"
                  className="rounded-full border-[#0f766e]/10 bg-white text-[10px] uppercase text-[#31575b]"
                >
                  <Pin className="mr-1 h-3 w-3" />
                  Pinned
                </Badge>
              ) : null}
            </div>
          </div>

          <div
            className="grid gap-3 overflow-y-auto p-4"
            style={{
              // Portal rendering frees the popover from parent clipping; this inner cap only prevents viewport overflow.
              maxHeight: surfaceMaxHeight,
            }}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="rounded-full border-[#0f766e]/10 bg-[#f4f8f7] text-[10px] uppercase text-[#31575b]"
              >
                {selectedSpan.scope}
              </Badge>
            </div>

            {selectedSpan.explanation || explanation ? (
              <div className="rounded-[1rem] border border-[#0f766e]/10 bg-[#f3f7f6] p-3">
                <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold tracking-[0.22em] text-[#5f7c80] uppercase">
                  <Sparkles className="h-3.5 w-3.5 text-[#0b6d70]" />
                  <span>Why This Highlight</span>
                </div>
                <p className="m-0 text-sm leading-6 break-words text-[#426064]">
                  {selectedSpan.explanation ?? explanation}
                </p>
              </div>
            ) : null}

            {competingSpans.length > 0 ? (
              <div className="grid gap-2">
                <span className="text-[10px] font-semibold tracking-[0.2em] text-[#5f7c80] uppercase">
                  Nearby Suggestions
                </span>
                <div className="flex flex-wrap gap-2">
                  {[selectedSpan, ...competingSpans].map((span) => (
                    <Button
                      key={span.id}
                      type="button"
                      size="xs"
                      variant="outline"
                      className={[
                        "rounded-full border-[#0f766e]/10 text-[10px] font-semibold uppercase shadow-none",
                        span.id === selectedSpan.id
                          ? "bg-[#daf0ee] text-[#0b6d70] hover:bg-[#daf0ee]"
                          : "bg-white text-[#557074] hover:bg-[#f5f8f7]",
                      ].join(" ")}
                      onClick={() => onSelectSpan(span.id)}
                    >
                      {span.sourceText}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="grid gap-2">
              {selectedSpan.candidates.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  className="grid gap-2 rounded-[1rem] border border-[#0f766e]/10 bg-white p-3 text-left transition-colors hover:border-[#8fd1ca] hover:bg-[#eef8f7]"
                  onClick={() => onApplyCandidate(selectedSpan.id, candidate)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="grid min-w-0 gap-1">
                      <strong className="text-sm break-words text-slate-950">
                        {candidate.text}
                      </strong>
                      {candidate.reason ? (
                        <span className="text-sm leading-6 break-words text-[#557074]">
                          {candidate.reason}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {candidate.recommended ? (
                        <Badge className="rounded-full bg-[#daf0ee] text-[10px] font-semibold tracking-[0.18em] text-[#0b6d70] uppercase hover:bg-[#daf0ee]">
                          Recommended
                        </Badge>
                      ) : null}
                      <ArrowRight className="h-4 w-4 text-[#87a0a1]" />
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-[#0f766e]/10 pt-2">
              <span className="min-w-0 break-words text-xs text-[#648084]">
                {pinned
                  ? coarsePointer
                    ? "Tap outside to close."
                    : "Press Esc or click outside to close."
                  : coarsePointer
                    ? "Tap a highlight to pin suggestions."
                    : "Hover previews here. Click a highlight to pin."}
              </span>
              {pinned ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-full border-[#0f766e]/10"
                  onClick={onClose}
                >
                  Close
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  if (typeof document === "undefined") {
    return popover;
  }

  return createPortal(popover, document.body);
}

function getPopoverStyle(anchorRect: PopoverAnchorRect) {
  const responsiveWidth = `min(${POPOVER_WIDTH}px, calc(100vw - ${POPOVER_VIEWPORT_MARGIN * 2}px))`;

  if (typeof window === "undefined") {
    return {
      width: responsiveWidth,
      maxWidth: responsiveWidth,
      left: anchorRect.left,
      top: anchorRect.bottom + POPOVER_GAP,
    };
  }

  const popoverWidth = Math.min(POPOVER_WIDTH, window.innerWidth - POPOVER_VIEWPORT_MARGIN * 2);
  const maxLeft = Math.max(
    POPOVER_VIEWPORT_MARGIN,
    window.innerWidth - popoverWidth - POPOVER_VIEWPORT_MARGIN,
  );
  const centeredLeft = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
  const left = Math.min(Math.max(POPOVER_VIEWPORT_MARGIN, centeredLeft), maxLeft);
  // The panel follows the highlighted text but must stay within the viewport so hover review works in narrow editors too.
  const showAbove =
    anchorRect.bottom + POPOVER_GAP + POPOVER_ESTIMATED_HEIGHT >
    window.innerHeight - POPOVER_VIEWPORT_MARGIN;

  return showAbove
    ? {
        width: responsiveWidth,
        maxWidth: responsiveWidth,
        left,
        top: Math.max(POPOVER_VIEWPORT_MARGIN, anchorRect.top - POPOVER_GAP),
        transform: "translateY(-100%)",
      }
    : {
        width: responsiveWidth,
        maxWidth: responsiveWidth,
        left,
        top: anchorRect.bottom + POPOVER_GAP,
      };
}

function getPopoverMaxHeight() {
  // Prefer the visual viewport so an on-screen keyboard (which shrinks it) keeps the
  // suggestion stack scrollable, and reserve clearance for a docked bottom nav.
  const fallback = `calc(100vh - ${POPOVER_VIEWPORT_MARGIN * 2 + POPOVER_BOTTOM_CLEARANCE}px)`;
  if (typeof window === "undefined") {
    return fallback;
  }

  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const available = viewportHeight - POPOVER_VIEWPORT_MARGIN * 2 - POPOVER_BOTTOM_CLEARANCE;
  return `${Math.max(160, available)}px`;
}
